import Backbone from 'backbone';
import Global from 'models/Global';
import config from 'config';
import { trackingPixelAnalytics } from '@bizzabo/analytics';
import FeatureToggleUtils from 'FeatureToggleUtils';
import WidgetAnalyticsProvider from '@bizzabo/widget-analytics/dist/WidgetAnalyticsProvider';
import { getModelOrSafeMock } from 'utils/backboneUtils';
import BootstrapData from 'models/BootstrapData';
import { gtmConstants } from 'constants/AnalyticsConst';

const normalizeMeModel = meModel => {
    if (!meModel) return;

    const me = getModelOrSafeMock(meModel);

    const userId = me.get('id');
    const email = me.get('primary-email');
    const isBizzaboer = email?.includes('bizzabo.com') ?? false;

    return {
        userId,
        email,
        isBizzaboer,
    };
};

const normalizeEventModel = eventModel => {
    if (!eventModel) return;

    const event = getModelOrSafeMock(eventModel);

    return {
        eventId: event.get('id'),
        eventName: event.get('name'),
        accountId: event.get('account-id'),
        status: event.get('status'),
        trackingPixelConfig: event.get('trackingPixel'),
    };
};

const getLocationDetails = () => {
    const { href, origin, pathname, search } = window.location ?? {};
    const urlPathWithQueryParams = href?.replace(origin, '') || '';

    return { urlPathWithQueryParams, href, origin, pathname, search };
};

const trackingEntryPoint = {
    selector: '#one-trust-cookie-auto-blocking',
    position: 'after',
};

const getEntryPoint = () => {
    const isSourcepointEnabled = !!FeatureToggleUtils.isFeatureEnabled('sourcepoint');
    const isConsentBlockingElemPresent = !!document.querySelector(trackingEntryPoint.selector);
    const isCookieConsentEnabled = BootstrapData('bootstrap-data-isCookieConsentEnabled');
    const shouldUseEntryPoint = isConsentBlockingElemPresent && (isCookieConsentEnabled || isSourcepointEnabled);

    return shouldUseEntryPoint ? trackingEntryPoint : null;
};

const Analytics = Backbone.Model.extend({
    initialize: function() {
        this.me = normalizeMeModel(Global.get('me'));
        this.event = normalizeEventModel(Global.get('event'));

        this.initHubspotTrackingCode();
        this.initFacebookTrackingCode();
        this.initGoogleAdwords();
        this.initGoogleTagManager();
        this.initAdroll();

        const ANALYTICS_INITIALIZED = 'BZ_WIDGET_ANALYTICS_PROVIDER_INITIALIZED';
        if (!window[ANALYTICS_INITIALIZED]) {
            new WidgetAnalyticsProvider({window});
            window[ANALYTICS_INITIALIZED] = true;
        }

        // TODO: @genechulkov, move all eventNames into /constants/AnalyticsConst.js
        this.listenTo(Backbone, 'analytics:formSubmitted', this.formSubmitted);
        this.listenTo(Backbone, 'analytics:routeChanged', this.onRouteChanged);

        this.listenTo(Backbone, 'analytics:saveSeat', this.saveSeat);
        this.listenTo(Backbone, 'analytics:sessionCheckIn', this.sessionCheckIn);
        this.listenTo(Backbone, 'analytics:sessionCheckOut', this.sessionCheckOut);
        this.listenTo(Backbone, 'analytics:downloadAttachment', this.downloadAttachment);
        this.listenTo(Backbone, 'analytics:addSessionToFavourites', this.addSessionToFavourites);
        this.listenTo(Backbone, 'analytics:removeSessionFromFavourites', this.removeSessionFromFavourites);
        this.listenTo(Backbone, 'analytics:openPoll', this.openPoll);
        this.listenTo(Backbone, 'analytics:submitPoll', this.submitPoll);
        this.listenTo(Backbone, 'analytics:viewPollResults', this.viewPollResults);
        this.listenTo(Backbone, 'analytics:shareSpeakerLink', this.shareSpeakerLink);
        this.listenTo(Backbone, 'analytics:shareSessionLink', this.shareSessionLink);
    },

    getTrackingPixelId: function() {
        const { trackingPixelConfig } = this.event ?? {};

        return trackingPixelConfig || {};
    },

    onRouteChanged: function() {
        const { urlPathWithQueryParams, href } = getLocationDetails();

        trackingPixelAnalytics.hubspotTrackingCode.sendPageView(urlPathWithQueryParams);
        trackingPixelAnalytics.adrollPixel.sendPageView();
        trackingPixelAnalytics.googleTagManager.sendPageView(href);
    },

    sendGTMEvent: function(eventName, payload = {}) {
        trackingPixelAnalytics.googleTagManager.sendEvent(eventName, payload);
    },

    saveSeat: function(payload) {
        this.sendGTMEvent(gtmConstants.events.saveSeat, payload);
    },

    sessionCheckIn: function(payload) {
        this.sendGTMEvent(gtmConstants.events.sessionCheckIn, payload);
    },

    sessionCheckOut: function(payload) {
        this.sendGTMEvent(gtmConstants.events.sessionCheckOut, payload);
    },

    downloadAttachment: function(payload) {
        this.sendGTMEvent(gtmConstants.events.downloadAttachment, payload);
    },

    addSessionToFavourites: function(payload) {
        this.sendGTMEvent(gtmConstants.events.addSessionToFavourites, payload);
    },

    removeSessionFromFavourites: function(payload) {
        this.sendGTMEvent(gtmConstants.events.removeSessionFromFavourites, payload);
    },

    openPoll: function(payload) {
        this.sendGTMEvent(gtmConstants.events.openPoll, payload);
    },

    submitPoll: function(payload) {
        this.sendGTMEvent(gtmConstants.events.submitPoll, payload);
    },

    viewPollResults: function(payload) {
        this.sendGTMEvent(gtmConstants.events.viewPollResults, payload);
    },

    shareSpeakerLink: function(payload) {
        this.sendGTMEvent(gtmConstants.events.shareSpeakerLink, payload);
    },

    shareSessionLink: function(payload) {
        this.sendGTMEvent(gtmConstants.events.shareSessionLink, payload);
    },

    initHubspotTrackingCode: function() {
        const { hubspotId } = this.getTrackingPixelId();

        if (!hubspotId) {
            return;
        }

        trackingPixelAnalytics.hubspotTrackingCode.initialize({
            token: hubspotId,
            insertPoint: getEntryPoint(),
        });
    },

    initFacebookTrackingCode: function() {
        const { facebookPixelId } = this.getTrackingPixelId();

        if (!facebookPixelId) {
            return;
        }

        trackingPixelAnalytics.facebookPixel.initialize({
            token: facebookPixelId,
            insertPoint: getEntryPoint(),
        });
        // NOTE: Please don't remove this!
        // Yes, usually 'PageView' event is sent after init automatically,
        // but in our case — it's not
        trackingPixelAnalytics.facebookPixel.sendPageView();
    },

    initGoogleAdwords: function() {
        const { googleAdwordsConversionId } = this.getTrackingPixelId();
        if (!googleAdwordsConversionId) {
            return;
        }

        trackingPixelAnalytics.googleAdWords.initialize({
            token: googleAdwordsConversionId,
            insertPoint: getEntryPoint(),
        });
    },

    initGoogleTagManager: function() {
        const {googleTagManagerId} = this.getTrackingPixelId();

        if (!googleTagManagerId) return;

        trackingPixelAnalytics.googleTagManager.initialize({
            token: googleTagManagerId,
            insertPoint: getEntryPoint(),
        });
    },

    formSubmitted: function(eventCategory, eventAction, eventLabel, eventValue, currency) {
        this.sendGTMEvent(gtmConstants.events.formSubmit, {
            eventCategory,
            eventAction,
            eventLabel,
            eventValue,
            currency,
        });
    },

    initAdroll: function() {
        const { adrollAdvId, adrollPixId } = this.getTrackingPixelId();

        if (!adrollAdvId || !adrollPixId) {
            return;
        }

        trackingPixelAnalytics.adrollPixel.initialize({
            token: {
                advId: adrollAdvId,
                pixId: adrollPixId,
            },
            insertPoint: getEntryPoint(),
        });
    },

    loadClarity: function() {
        const doNotLoadClarity = FeatureToggleUtils.isFeatureEnabled('strictlyNecessaryCookies');
        const clarityKey = config.clarityKey;

        if (doNotLoadClarity || !clarityKey) {
            return;
        }

        const isCookieConsentEnabled = BootstrapData('bootstrap-data-isCookieConsentEnabled') === true;

        (function(c, l, a, r, i, t, y){
            c[a]=c[a]||function(){
                (c[a].q=c[a].q||[]).push(arguments);
            };
            t=l.createElement(r);t.async=1;t.src='https://www.clarity.ms/tag/'+i;
            /** COOKIE CONSENT - START */
            if (c.__scriptTagTypeAttribute) {
                t.type = c.__scriptTagTypeAttribute;
            }
            t.dataset.gdpr = 'Develop and improve products';
            t.dataset.ccpa = 'Do Not Sell or Share My Personal Information';

            // OneTrust cookie consent - if cookie consent is enabled, we need to set the script tag type to "text/plain" and add the class name that OneTrust listens to in order to block the script until consent is given
            if (isCookieConsentEnabled) {
                t.type = 'text/plain';
                t.className = 'optanon-category-C0002';
            }
            /** COOKIE CONSENT - END */
            t.onload = function() {
                c.clarity('consent');
            };
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t, y);
        })(window, document, 'clarity', 'script', clarityKey);
    },

    setClarityIdentity: function({ eventId, email, displayName }) {
        /**
         * Clarity Custom Filters:
         *
         * "custom-id":         userId
         * "custom-session-id": eventId
         * "custom-page-id":    email
         * "friendly-name":     displayName
         *
         *  */
        if (!window.clarity || !email) {
            return;
        }

        window.clarity('identify', email, eventId, email, displayName);
        window.clarity('set', 'eventId', eventId);
        window.clarity('set', 'user', email);
    },
});

export default Analytics;
